/* DoctorCaseCommandHeader — iter101.59
 *
 * Case-detail header for the Doctor portal. Composes the shared
 * /components/shared/CommandHeaderShell so the doctor sees the same
 * editorial "command bar" as the planner when they look at the same
 * case. Differences from the planner variant:
 *
 *   · rightSlot is the doctor's action toolbar (Submit, Send back,
 *     Confirm receipt, Edit, Cancel, Download PDF menu) instead of
 *     the status pill. The status pill moves to a dedicated row below
 *     because the doctor has too many CTAs to fit alongside it in the
 *     right slot.
 *   · Compliance chip becomes an interactive chooser
 *     (ComplianceChipChooser) when the doctor is still able to flip
 *     to Invisique-managed compliance.
 *   · Assignment stack is Clinic-only (a doctor is always their own
 *     provider — showing "Provider: Dr. me" is noise).
 *
 * Props mirror the ones the old PatientHeader inside CaseDetail.jsx
 * received, so wiring is a drop-in replacement.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Buildings, Hash, CalendarBlank, PaperPlaneTilt, PencilSimple,
    Sparkle, Timer, ScanSmiley, ShieldCheck, Stethoscope, CheckCircle,
    Phone, CaretDown,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { toast } from "sonner";
import api from "@/lib/api";
import { CaseStatusBadge } from "@/components/shared/CaseStatusBadge";
import CommandHeaderShell from "@/components/shared/CommandHeaderShell";
import PatientAvatar from "@/components/patient/PatientAvatar";
import { ConfirmReceiptDialog } from "@/components/case/ConfirmReceiptDialog";


/* ── InlineComplianceChooser ─────────────────────────────────────────
 * Slim variant of the legacy ComplianceChipChooser tuned to sit inside
 * a DataCell's value slot (caps label is provided by the shell, so this
 * component only owns the interactive value line). Reuses the same
 * `/cases/{id}/compliance-mode` upgrade API + Popover panel so the
 * doctor's decision flow is identical to the old chip.
 * iter101.63bug — Fixes the overflow reported in the design QA:
 * previously the full-fat chooser rendered its own "Compliance ·
 * Upgrade to Invisique-managed" pill inside a cell that already had a
 * "COMPLIANCE" caps label above it, so the pill overflowed the row.
 * ─────────────────────────────────────────────────────────────────── */
function InlineComplianceChooser({ caseId, onChosen }) {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [fee, setFee] = useState(null);

    useEffect(() => {
        if (!open || fee !== null) return;
        (async () => {
            try {
                const r = await api.get("/settings/compliance-fee");
                setFee(typeof r?.data?.fee === "number" ? r.data.fee : 0);
            } catch {
                setFee(0);
            }
        })();
    }, [open, fee]);

    const upgrade = async () => {
        if (saving) return;
        setSaving(true);
        try {
            const r = await api.patch(`/cases/${caseId}/compliance-mode`, { mode: "invisique" });
            const charged = r?.data?.fee_charged;
            const invoiceUpdated = r?.data?.invoice_updated;
            toast.success(
                invoiceUpdated
                    ? `Invisique™ will manage compliance · ₹${(charged || 0).toLocaleString("en-IN")} added to invoice`
                    : "Invisique™ will manage compliance on this case"
            );
            setOpen(false);
            if (onChosen) onChosen();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not upgrade compliance mode");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-rose-800 hover:text-rose-900 leading-tight truncate group"
                    data-testid="case-monitoring-mode-empty"
                    title="Upgrade to Invisique-managed compliance"
                    aria-label="Upgrade to Invisique-managed compliance"
                >
                    <span className="truncate">Upgrade to Invisique™</span>
                    <CaretDown size={9} weight="bold" className="opacity-70 group-hover:opacity-100 shrink-0" />
                </button>
            </PopoverTrigger>
            <PopoverContent side="bottom" align="start" className="w-80 p-3 space-y-2">
                <div className="pb-2 border-b border-border/60">
                    <div className="text-[13px] font-semibold">Upgrade to Invisique-managed compliance</div>
                    <div className="text-[11px] text-muted-foreground mt-1 leading-snug">
                        You&apos;re currently managing this patient&apos;s compliance yourself. Handing it to Invisique™ enables our CoE-Support team to run check-ins, wear-time reminders and monitoring.
                    </div>
                </div>
                <div className="flex items-start gap-2.5 p-2 rounded-md bg-primary/5 border border-primary/15">
                    <ShieldCheck size={20} weight="duotone" className="text-primary shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                        <div className="text-[12.5px] font-semibold text-foreground">Invisique™ manages it</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                            Check-ins, reminders, wear-time monitoring — done for you.
                        </div>
                        <div className="mt-1.5 text-[11px] font-semibold">
                            {fee === null && (
                                <span className="text-muted-foreground">Loading fee…</span>
                            )}
                            {fee !== null && fee <= 0 && (
                                <span className="text-emerald-700">Included · no extra charge</span>
                            )}
                            {fee !== null && fee > 0 && (
                                <span className="text-foreground">
                                    ₹{fee.toLocaleString("en-IN")} <span className="text-muted-foreground font-normal">— added to this case&apos;s invoice</span>
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="text-[10.5px] text-muted-foreground leading-snug px-0.5">
                    One-way upgrade · locks once aligners are fit on patient.
                </div>
                <button
                    type="button"
                    onClick={upgrade}
                    disabled={saving || fee === null}
                    className="w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-md bg-primary text-primary-foreground font-semibold text-[12.5px] hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="case-compliance-upgrade-confirm"
                >
                    {saving ? "Upgrading…" : "Confirm upgrade"}
                </button>
            </PopoverContent>
        </Popover>
    );
}


function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", {
            day: "2-digit", month: "short", year: "numeric",
        }).replace(/\s/g, "-");
    } catch {
        return "—";
    }
}


export default function DoctorCaseCommandHeader({
    patient,
    caseObj,
    clinicName,
    caseStatusKey,
    caseCode,
    caseId,
    onSubmit,
    onResubmitAfterInfo,
    onConfirmedReceipt,
    shippingCarrier,
    trackingNumber,
    onComplianceChosen,
    humanPatientId,
}) {
    const [confirmOpen, setConfirmOpen] = useState(false);

    const c = caseObj || {};
    const pricing = c.pricing || {};

    /* ── Derive data-strip values. Same source of truth as the planner
       header so both roles see identical chip content. ── */
    const priorityCode = pricing.priority_code || "standard";
    const priorityLabel = pricing.priority_label || "Standard";
    const priorityTat = pricing.priority_tat_days;
    const mfgValue = priorityCode === "standard"
        ? `Standard${priorityTat ? ` · ${priorityTat}d TAT` : ""}`
        : `${priorityLabel}${priorityTat ? ` · ${priorityTat}d TAT` : ""}`;

    const ioPay = pricing.io_scanning_payment_choice;
    const ioFee = pricing.scanner_pickup_fee;
    const hasIoScan = Boolean(ioPay)
        || (typeof ioFee === "number" && ioFee > 0)
        || Boolean(pricing.scanner_pickup_requested);
    const ioValue = hasIoScan ? "Requested" : "Not requested";

    const complianceUpgraded = c.coe_support_compliance === true;
    /* Same lock set the CaseInfoChipRow uses. Once the case has moved
       past the point where compliance is meaningfully changeable, we
       stop offering the chooser. */
    const LOCKED_COMPLIANCE_STATUSES = new Set([
        "in_treatment", "treatment_started", "refinement_requested",
        "completed", "cancelled",
    ]);
    const complianceLocked = LOCKED_COMPLIANCE_STATUSES.has(caseStatusKey);
    const canPickCompliance = !complianceUpgraded && !complianceLocked;
    const complianceValue = complianceUpgraded ? "Invisique™ manages" : "Self-managed";
    const complianceTone = complianceUpgraded ? "emerald" : (complianceLocked ? "slate" : "rose");

    const planningValue = c.planning_mode === "doctor" ? "Self-planned" : "Invisique™ Team";

    /* Demographics — same shape as the planner header. Human-scoped ID
       goes last so the mono treatment in the shell kicks in. */
    const ageStr = patient?.age ? `${patient.age} yr` : null;
    const genderStr = patient?.gender
        ? patient.gender.charAt(0).toUpperCase() + patient.gender.slice(1)
        : null;
    const idStr = humanPatientId
        ? humanPatientId(patient)
        : (patient?.human_id || `INV-PT-${(patient?.id || "").slice(0, 6).toUpperCase()}`);
    const demographics = [ageStr, genderStr, idStr].filter(Boolean);

    const demographicsSuffix = patient?.id ? (
        <Link
            to={`/doctor/patients/${patient.id}`}
            className="text-primary hover:underline font-semibold"
            data-testid="case-patient-profile-link"
        >
            Open profile
        </Link>
    ) : null;

    /* iter101.89 — Right slot trimmed. Edit + Cancel + Download PDF menu
       removed per user direction: doctors don't need those actions
       cluttering the header. Kept only the status-tied primary CTAs
       (Submit / Send back / Confirm receipt) — the destination-defining
       actions the doctor is here to take. */
    const rightSlot = (
        <div
            className="flex items-center justify-end gap-1.5 flex-wrap"
            data-testid="case-header-actions"
        >
            {onSubmit && (
                <Button
                    onClick={onSubmit}
                    size="sm"
                    className="rounded-full h-7 text-[11px]"
                    data-testid={`submit-case-${caseId}`}
                >
                    <PaperPlaneTilt size={11} weight="fill" className="mr-1" /> Submit for planning
                </Button>
            )}
            {onResubmitAfterInfo && (
                <Button
                    onClick={onResubmitAfterInfo}
                    size="sm"
                    className="rounded-full h-7 text-[11px] bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid={`resubmit-info-${caseId}`}
                >
                    <PaperPlaneTilt size={11} weight="fill" className="mr-1" /> Send back to planning
                </Button>
            )}
            {onConfirmedReceipt && (
                <Button
                    onClick={() => setConfirmOpen(true)}
                    size="sm"
                    className="rounded-full h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`confirm-receipt-${caseId}`}
                >
                    <CheckCircle size={11} weight="fill" className="mr-1" /> Confirm receipt
                </Button>
            )}
        </div>
    );

    /* Optional shipment stripe — surfaces carrier + tracking when the
       case has shipped and the doctor still needs to confirm receipt. */
    const stripe = (onConfirmedReceipt && (shippingCarrier || trackingNumber)) ? (
        <div
            className="border-t border-emerald-200/60 bg-emerald-50/70 px-5 py-1.5 flex items-center gap-2 text-[11px]"
            data-testid="case-header-shipment-stripe"
        >
            <span className="font-bold tracking-[0.16em] uppercase text-emerald-700">Shipment</span>
            {shippingCarrier && <span className="font-semibold text-emerald-900">{shippingCarrier}</span>}
            {shippingCarrier && trackingNumber && <span className="text-emerald-700/50">·</span>}
            {trackingNumber && <span className="font-mono text-emerald-900">{trackingNumber}</span>}
        </div>
    ) : null;

    /* Chip strip. The Compliance cell uses the `render` escape hatch
       when the doctor can still pick — the inline chooser fits the
       DataCell pattern (caps label on top + slim clickable value
       below). iter101.63bug — Replaced the full-fat
       ComplianceChipChooser with an inline slim variant so the pill
       doesn't overflow the strip cell. */
    const complianceCell = canPickCompliance ? {
        render: (
            <div className="px-3.5 py-2.5 min-w-0" title="Choose who handles compliance monitoring on this case">
                <div className="flex items-center gap-1 mb-1">
                    <Stethoscope size={10} weight="fill" className="text-rose-700" />
                    <span className="text-[9px] font-bold tracking-[0.14em] uppercase text-teal-800/70">
                        Compliance
                    </span>
                </div>
                <div data-testid="case-monitoring-mode-empty-doctor">
                    <InlineComplianceChooser
                        caseId={caseObj?.id}
                        onChosen={() => onComplianceChosen && onComplianceChosen()}
                    />
                </div>
            </div>
        ),
    } : {
        icon: complianceUpgraded ? ShieldCheck : Stethoscope,
        label: "Compliance",
        value: complianceValue,
        tone: complianceTone,
        testid: complianceUpgraded ? "case-monitoring-mode-invisique" : "case-monitoring-mode-empty-readonly",
        title: complianceUpgraded
            ? "Invisique™ runs patient-side compliance & monitoring on this case"
            : "Doctor is self-managing compliance on this case",
    };

    return (
        <>
            <CommandHeaderShell
                testId="doctor-case-command-header"
                avatar={<PatientAvatar patient={patient} size="xl" className="ring-2 ring-primary/15" />}
                identityLabel="Patient · Case"
                name={patient?.name || "Unnamed patient"}
                nameTitle={patient?.name}
                demographics={demographics}
                demographicsSuffix={demographicsSuffix}
                assignments={[
                    /* Only show Clinic — showing "Provider: Dr. me" is
                       noise on the doctor's own case. If we ever add
                       staff impersonation this can grow to 2 rows. */
                    {
                        label: "Clinic",
                        value: clinicName || "—",
                        icon: Buildings,
                        testid: "case-header-clinic-name",
                        title: clinicName,
                    },
                    /* Phone comes second so the doctor always has
                       one-click contact info visible without leaving
                       the case page. */
                    ...(patient?.phone ? [{
                        label: "Patient phone",
                        value: patient.phone,
                        icon: Phone,
                        testid: "case-header-patient-phone",
                        title: `Call ${patient.name}`,
                    }] : []),
                ]}
                rightSlot={rightSlot}
                statusPill={(
                    <span data-testid="case-status-pill">
                        <CaseStatusBadge status={caseStatusKey} size="md" portal="doctor" />
                    </span>
                )}
                chips={[
                    { icon: Hash, label: "Case ID", value: caseCode || "—", mono: true, testid: "case-chip-code", title: caseCode },
                    { icon: CalendarBlank, label: "Created", value: fmtDate(c.created_at), testid: "case-chip-created" },
                    {
                        icon: c.planning_mode === "doctor" ? PencilSimple : Sparkle,
                        label: "Planning",
                        value: planningValue,
                        testid: c.planning_mode === "doctor" ? "case-planning-mode-self" : "case-planning-mode-coe",
                        title: c.planning_mode === "doctor"
                            ? "The doctor authored the treatment plan on this case"
                            : "Invisique™ Planning Team authored the treatment plan",
                    },
                    {
                        icon: Timer,
                        label: "Manufacturing",
                        value: mfgValue,
                        testid: "case-chip-manufacturing",
                        title: priorityTat ? `Expected manufacturing turnaround: ${priorityTat} days` : "Manufacturing turnaround by tier",
                    },
                    {
                        icon: ScanSmiley,
                        label: "IO Scans",
                        value: ioValue,
                        testid: "case-chip-io-scan",
                        title: hasIoScan
                            ? "Invisique will send an IO-scanning partner to the clinic"
                            : "Doctor submits their own PVS impressions or intraoral scans",
                    },
                    complianceCell,
                ]}
                stripe={stripe}
            />

            {onConfirmedReceipt && (
                <ConfirmReceiptDialog
                    caseId={caseId}
                    caseCode={caseCode}
                    trackingNumber={trackingNumber}
                    carrier={shippingCarrier}
                    open={confirmOpen}
                    onClose={() => setConfirmOpen(false)}
                    onConfirmed={() => { setConfirmOpen(false); onConfirmedReceipt?.(); }}
                />
            )}
        </>
    );
}
